// GatewayTokensClient covers sandbox-scoped FOA bearer tokens (the
// post-redesign replacement for the legacy RepoAiApiKey rows). Each
// sandbox can have multiple tokens for rotation / dev-prod separation.
//
// The plaintext bearer is returned EXACTLY ONCE — at create time. The
// frontend must surface it ("copy now") before navigating away; once
// the response is dropped, only the encrypted envelope persists and the
// plaintext is unrecoverable.
import type { BaseClient } from '../../http/BaseClient.js';
import {
  CreateGatewayTokenRequest,
  CreateGatewayTokenResponse,
  GatewayToken,
  SimpleKeysResponse,
  WireGatewayToken,
  normalizeGatewayToken,
} from './types.js';

export class GatewayTokensClient {
  constructor(private readonly http: BaseClient) {}

  async list(sandboxId: string): Promise<GatewayToken[]> {
    const res = await this.http.get<{ success: boolean; tokens?: WireGatewayToken[]; data?: WireGatewayToken[] }>(
      `/sandbox/${sandboxId}/gateway-token`,
    );
    const list = res.tokens ?? res.data ?? [];
    return list.map(normalizeGatewayToken);
  }

  /**
   * create returns both the persisted token row and the plaintext bearer
   * (`secret`). The bearer is shown only here; surface it to the user
   * immediately. If they navigate away without copying, they have to
   * delete the token and create a new one.
   */
  async create(sandboxId: string, body: CreateGatewayTokenRequest): Promise<CreateGatewayTokenResponse> {
    const res = await this.http.post<{
      success: boolean;
      token?: WireGatewayToken;
      secret?: string;
    }>(`/sandbox/${sandboxId}/gateway-token`, { body });
    if (!res.token || !res.secret) {
      throw new Error('createGatewayToken response missing token or secret.');
    }
    return {
      token: normalizeGatewayToken(res.token),
      secret: res.secret,
    };
  }

  delete(sandboxId: string, tokenId: string): Promise<SimpleKeysResponse> {
    return this.http.delete<SimpleKeysResponse>(`/sandbox/${sandboxId}/gateway-token/${tokenId}`);
  }
}
